// Les paliers de bien-être et les événements qu'ils déclenchent (coup de blues, épuisement).
import { MOODS, type MoodDef } from '../content/moods';
import { nextRandom } from '../engine/rng';
import type { GameState } from '../engine/state';
import { LOW_ENERGY_THRESHOLD, addWellbeing } from './actions';
import { startEvent } from './events';

/** Chance par semaine de jeu d'un coup de blues quand le moral est au plus bas. */
export const BLUES_WEEKLY_CHANCE = 0.1;
export const BLUES_MIN_DAYS = 90;
export const EXHAUSTION_MIN_DAYS = 180;
/** Un point de bien-être perdu toutes les 10 secondes réelles passées à plat. */
export const LOW_ENERGY_DRAIN_SECONDS = 10;

export function currentMood(state: GameState): MoodDef {
  return MOODS.find((mood) => state.wellbeing >= mood.min) ?? MOODS[MOODS.length - 1];
}

/** À plat : depuis le passage sous 4 d'énergie, jusqu'à ce qu'elle remonte à 10. */
export function isLowEnergy(state: GameState): boolean {
  return state.energyUnlocked && (state.lowEnergyHit || state.energy < LOW_ENERGY_THRESHOLD);
}

/** Appelé à chaque battement, seulement quand le temps passe (pas pendant une décision). */
export function applyLowEnergyMalus(state: GameState, realMs: number): void {
  if (!isLowEnergy(state)) return;
  addWellbeing(state, -(state.speed.dev * realMs) / 1000 / LOW_ENERGY_DRAIN_SECONDS);
  checkExhaustion(state);
}

/**
 * L'épuisement tombe dès que le bien-être passe sous 10 : appelé après chaque heure de travail
 * en plus, et chaque semaine de jeu. Renvoie vrai s'il a été déclenché.
 */
export function checkExhaustion(state: GameState): boolean {
  if (state.pendingEvent || !state.energyUnlocked) return false;
  if (currentMood(state).id !== 'epuisement' || state.day < state.restUntilDay) return false;
  if (state.day - state.lastExhaustionDay < EXHAUSTION_MIN_DAYS) return false;
  state.lastExhaustionDay = state.day;
  startEvent(state, 'epuisement');
  return true;
}

/** Appelé une fois par semaine de jeu ; renvoie vrai si un événement a été déclenché. */
export function maybeMoodEvent(state: GameState): boolean {
  if (state.pendingEvent) return false;
  if (checkExhaustion(state)) return true;
  const resting = state.day < state.restUntilDay;
  const mood = currentMood(state).id;
  if (
    mood === 'coupDeMou' &&
    !resting &&
    state.day - state.lastBluesDay >= BLUES_MIN_DAYS &&
    nextRandom(state) < BLUES_WEEKLY_CHANCE
  ) {
    state.lastBluesDay = state.day;
    startEvent(state, 'coup_de_blues');
    return true;
  }
  return false;
}
